export const DEFAULT_RANGE_USD = 1000
export const DEFAULT_TARGET_PCT = 0.006

export function resolveTargetPct(frac) {
  const n = Number(frac)
  return n > 0 ? n : DEFAULT_TARGET_PCT
}

export function resolveRangeUsd(rangeUsd) {
  const n = Number(rangeUsd)
  if (!Number.isFinite(n) || n < 0) return DEFAULT_RANGE_USD
  return n
}

export function longTargetPrice(entryPrice, targetPct = DEFAULT_TARGET_PCT) {
  const entry = Number(entryPrice)
  if (!(entry > 0)) return 0
  return entry * (1 + resolveTargetPct(targetPct))
}

export function shortTargetPrice(entryPrice, targetPct = DEFAULT_TARGET_PCT) {
  const entry = Number(entryPrice)
  if (!(entry > 0)) return 0
  return entry * (1 - resolveTargetPct(targetPct))
}

export function isWithinRange(price, marketPrice, rangeUsd = DEFAULT_RANGE_USD) {
  const p = Number(price)
  const m = Number(marketPrice)
  if (!(p > 0) || !(m > 0)) return false
  const range = resolveRangeUsd(rangeUsd)
  if (range === 0) return true
  return Math.abs(p - m) <= range
}

function toOrder({
  id,
  side,
  action,
  entryPrice,
  qty,
  targetPrice,
  marketPrice,
  rangeUsd,
}) {
  const distanceUsd = targetPrice - Number(marketPrice || 0)
  return {
    id,
    side,
    action,
    entryPrice,
    targetPrice,
    qty,
    distanceUsd,
    absDistanceUsd: Math.abs(distanceUsd),
    inRange: isWithinRange(targetPrice, marketPrice, rangeUsd),
  }
}

function clusterKey(order) {
  return `${order.side}:${Math.round(order.targetPrice)}`
}

export function clusterDesovaOrders(orders = []) {
  const map = new Map()

  orders.forEach((order) => {
    const key = clusterKey(order)
    if (!map.has(key)) {
      map.set(key, {
        key,
        side: order.side,
        action: order.action,
        targetPrice: 0,
        qty: 0,
        unitCount: 0,
        distanceUsd: 0,
        absDistanceUsd: 0,
        orders: [],
      })
    }
    map.get(key).orders.push(order)
  })

  return Array.from(map.values())
    .map((cluster) => {
      const qty = cluster.orders.reduce((sum, order) => sum + order.qty, 0)
      const targetNotional = cluster.orders.reduce(
        (sum, order) => sum + order.targetPrice * order.qty,
        0
      )
      const distanceNotional = cluster.orders.reduce(
        (sum, order) => sum + order.distanceUsd * order.qty,
        0
      )
      const targetPrice = qty > 0 ? targetNotional / qty : 0
      const distanceUsd = qty > 0 ? distanceNotional / qty : 0
      return {
        ...cluster,
        qty,
        unitCount: cluster.orders.length,
        targetPrice,
        distanceUsd,
        absDistanceUsd: Math.abs(distanceUsd),
      }
    })
    .sort((a, b) => b.targetPrice - a.targetPrice)
}

export function spreadBetween(buyPrice, sellPrice) {
  const buy = Number(buyPrice)
  const sell = Number(sellPrice)
  if (!(buy > 0) || !(sell > 0)) {
    return { dollarUsd: 0, pct: 0 }
  }
  const dollarUsd = Math.abs(sell - buy)
  const mid = (buy + sell) / 2
  return {
    dollarUsd,
    pct: mid > 0 ? (dollarUsd / mid) * 100 : 0,
  }
}

function toSpreadPair(buy, sell, rank) {
  const metrics = spreadBetween(buy.targetPrice, sell.targetPrice)
  return {
    key: `${buy.key}|${sell.key}`,
    rank,
    isFirst: rank === 0,
    side: 'inner',
    buyKey: buy.key,
    sellKey: sell.key,
    buyPrice: buy.targetPrice,
    sellPrice: sell.targetPrice,
    priceFrom: buy.targetPrice,
    priceTo: sell.targetPrice,
    dollarUsd: metrics.dollarUsd,
    pct: metrics.pct,
  }
}

function toStepBand({ key, side, fromPrice, toPrice, buyPrice, sellPrice, rank }) {
  const metrics = spreadBetween(buyPrice, sellPrice)
  return {
    key,
    rank,
    isFirst: false,
    side,
    buyPrice,
    sellPrice,
    priceFrom: fromPrice,
    priceTo: toPrice,
    dollarUsd: metrics.dollarUsd,
    pct: metrics.pct,
  }
}

/**
 * Primeira compra = BUY mais alto. Primeira venda = SELL mais baixo.
 * Demais = degraus sem overlap: cada compra extra até a anterior,
 * cada venda extra até a anterior. O % é contra a 1ª do lado oposto.
 */
export function pairDesovaSpreads(clusters = []) {
  const buys = clusters
    .filter((cluster) => cluster.action === 'buy' && cluster.targetPrice > 0)
    .sort((a, b) => b.targetPrice - a.targetPrice)
  const sells = clusters
    .filter((cluster) => cluster.action === 'sell' && cluster.targetPrice > 0)
    .sort((a, b) => a.targetPrice - b.targetPrice)

  if (!buys.length || !sells.length) {
    return { first: null, rest: [] }
  }

  const firstBuy = buys[0]
  const firstSell = sells[0]
  const first = toSpreadPair(firstBuy, firstSell, 0)
  const rest = []
  let rank = 1

  for (let i = 1; i < buys.length; i += 1) {
    rest.push(
      toStepBand({
        key: `buy-step-${buys[i].key}`,
        side: 'buy',
        fromPrice: buys[i].targetPrice,
        toPrice: buys[i - 1].targetPrice,
        buyPrice: buys[i].targetPrice,
        sellPrice: firstSell.targetPrice,
        rank,
      })
    )
    rank += 1
  }

  for (let i = 1; i < sells.length; i += 1) {
    rest.push(
      toStepBand({
        key: `sell-step-${sells[i].key}`,
        side: 'sell',
        fromPrice: sells[i - 1].targetPrice,
        toPrice: sells[i].targetPrice,
        buyPrice: firstBuy.targetPrice,
        sellPrice: sells[i].targetPrice,
        rank,
      })
    )
    rank += 1
  }

  return { first, rest }
}

/**
 * Mapeia o livro ativo para as ordens de desova (target de cada unit).
 * Entrada já é um snapshot neutro — sem Vue/Vuex.
 *
 * @param {object} input
 * @param {Array<{id?: string, price: number, qty: number}>} input.longs
 * @param {Array<{id?: string, price: number, qty: number}>} input.shorts
 * @param {number} input.marketPrice
 * @param {number} input.targetPct  fração (ex.: 0.006)
 * @param {number} input.rangeUsd  0 = todas; >0 = |target − mercado| ≤ range
 */
export function computeDesovaOrders({
  longs = [],
  shorts = [],
  marketPrice = 0,
  targetPct = DEFAULT_TARGET_PCT,
  rangeUsd = DEFAULT_RANGE_USD,
} = {}) {
  const pct = resolveTargetPct(targetPct)
  const range = resolveRangeUsd(rangeUsd)
  const orders = []

  longs.forEach((lot, index) => {
    const entryPrice = Number(lot.price || 0)
    const qty = Number(lot.qty || 0)
    if (entryPrice <= 0 || qty <= 0) return
    orders.push(
      toOrder({
        id: lot.id || `long-${index}`,
        side: 'long',
        action: 'sell',
        entryPrice,
        qty,
        targetPrice: longTargetPrice(entryPrice, pct),
        marketPrice,
        rangeUsd: range,
      })
    )
  })

  shorts.forEach((lot, index) => {
    const entryPrice = Number(lot.price || 0)
    const qty = Number(lot.qty || 0)
    if (entryPrice <= 0 || qty <= 0) return
    orders.push(
      toOrder({
        id: lot.id || `short-${index}`,
        side: 'short',
        action: 'buy',
        entryPrice,
        qty,
        targetPrice: shortTargetPrice(entryPrice, pct),
        marketPrice,
        rangeUsd: range,
      })
    )
  })

  orders.sort((a, b) => b.targetPrice - a.targetPrice)

  const visible = orders.filter((order) => order.inRange)
  const clusters = clusterDesovaOrders(visible)

  return {
    orders,
    clusters,
    spreads: pairDesovaSpreads(clusters),
    plottedCount: visible.length,
    totalCount: orders.length,
    rangeUsd: range,
    targetPct: pct,
  }
}
